import type { BaseHistogramGlobalFilterItem, GlobalFilterItem } from '@weberus/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getOnlyNumbers, numberFormat } from '@/app/_shared/utils/number-utils';
import { hasItems } from '@/app/_shared/utils/has-items';
import {
  calculateInputValue,
  calculateInputValueByBins,
  calculatePosition,
  roundDownToHundred,
} from '../histogram.utils';

const isTouchEventType = (
  e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent,
): e is TouchEvent | React.TouchEvent => {
  return 'touches' in e;
};

interface UseHistogramDragHandlerProps extends BaseHistogramGlobalFilterItem {
  type: 'HISTOGRAM_NUMBER_RANGE' | 'FLAT_NUMBER_RANGE_WITHOUT_UPPER_LIMIT';
  /** 히스토그램의 범위가 *최종* 변경될 때 호출되는 콜백 */
  onRangeChange: (min: number, max: number) => void;
  onClickHistogramHandle: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  onFocusHistogramInput: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  /** 히스토그램이 선택되었는지 여부 */
  isSelected: boolean;
}

/** 히스토그램의 드래그 핸들러를 관리하는 훅 */

const useHistogramDragHandler = ({
  onRangeChange,
  onClickHistogramHandle,
  onFocusHistogramInput,
  isSelected,
  ...item
}: UseHistogramDragHandlerProps) => {
  const { bins, range, items } = item;
  const initValue = useMemo(() => items?.[0].value, [items]);
  const initLeftPos = calculatePosition({ bins, value: initValue.from, range });
  const initRightPos = calculatePosition({ bins, value: initValue.to, range });

  const [leftHandlePosition, setLeftHandlePosition] = useState(initLeftPos); // 핸들의 위치 (0 ~ 100)
  const [rightHandlePosition, setRightHandlePosition] = useState(initRightPos); // 핸들의 위치 (0 ~ 100)

  // LEFT와 RIGHT는 초기 위치에 따른 값으로, 핸들의 위치는 교차될 수 있습니다. (즉 RIGHT가 LEFT보다 왼쪽으로 이동할 수 있습니다)
  const [draggingHandle, setDraggingHandle] = useState<'LEFT' | 'RIGHT' | null>(null);
  const [initialMouseX, setInitialMouseX] = useState(0); // 드래그 시작 시 마우스 위치
  const [initialHandlePosition, setInitialHandlePosition] = useState(0); // 드래그 시작 시 핸들 위치

  const [zIndexLeft, setZIndexLeft] = useState<number>(0);
  const [zIndexRight, setZIndexRight] = useState<number>(0);

  const histogramRef = useRef<HTMLDivElement>(null);

  // 최소, 최대 입력값
  const [minInputValue, setMinInputValue] = useState<string>(numberFormat(initValue.from));
  const [maxInputValue, setMaxInputValue] = useState<string>(numberFormat(initValue.to));

  const onHandleDragStart = (handle: 'LEFT' | 'RIGHT') => (e: React.MouseEvent | React.TouchEvent) => {
    // 클릭 시점의 좌측 핸들을 min, 우측 핸들을 max로 설정
    const minMax = (handle === 'LEFT') === leftHandlePosition <= rightHandlePosition ? 'min' : 'max';
    onClickHistogramHandle(item.serverLogMeta, minMax);

    setDraggingHandle(handle);

    const clientX = isTouchEventType(e) ? e.touches[0].clientX : e.clientX;
    setInitialMouseX(clientX);
    setInitialHandlePosition(handle === 'LEFT' ? leftHandlePosition : rightHandlePosition);

    // 드래그 중인 핸들을 항상 위에 표시하기 위해 zIndex를 조정합니다.
    setZIndexLeft(handle === 'LEFT' ? 1 : 0);
    setZIndexRight(handle === 'RIGHT' ? 1 : 0);
    e.preventDefault();
    e.stopPropagation();
  };

  const onHandleMouseMove = (e: MouseEvent | TouchEvent): void => {
    if (!draggingHandle || !histogramRef.current) return;

    const clientX = isTouchEventType(e) ? e.touches[0].clientX : e.clientX;
    const dx = clientX - initialMouseX; // 드래그하는 동안 이동한 픽셀 (x축 변화량)
    const percentageMove = (dx / histogramRef.current.clientWidth) * 100; // 픽셀을 백분율로 변환

    const setPosition = draggingHandle === 'LEFT' ? setLeftHandlePosition : setRightHandlePosition;
    const newValue = Math.max(0, Math.min(initialHandlePosition + percentageMove, 100));
    setPosition(newValue);

    const left = draggingHandle === 'LEFT' ? newValue : leftHandlePosition;
    const right = draggingHandle === 'RIGHT' ? newValue : rightHandlePosition;
    const min = Math.min(left, right);
    const max = Math.max(left, right);

    if (hasItems(bins)) {
      // 히스토그램이 있는 경우, 각 구간에 따른 값을 계산
      setMinInputValue(calculateInputValueByBins(bins, min));
      setMaxInputValue(calculateInputValueByBins(bins, max));
    } else {
      // 히스토그램이 없는 경우, 단순 범위 계산
      setMinInputValue(calculateInputValue(range.min, range.max, min));
      setMaxInputValue(calculateInputValue(range.min, range.max, max));
    }
  };

  // 최신 값을 저장할 ref
  const minInputValueRef = useRef(minInputValue);
  const maxInputValueRef = useRef(maxInputValue);

  // 상태가 변경될 때 ref도 업데이트
  useEffect(() => {
    minInputValueRef.current = minInputValue;
  }, [minInputValue]);
  useEffect(() => {
    maxInputValueRef.current = maxInputValue;
  }, [maxInputValue]);

  const onHandleMouseUp = useCallback(() => {
    setDraggingHandle(null);
    onRangeChange(Number(getOnlyNumbers(minInputValueRef.current)), Number(getOnlyNumbers(maxInputValueRef.current)));
  }, [onRangeChange]);

  const handlePosition = useMemo(() => {
    return {
      min: Math.min(leftHandlePosition, rightHandlePosition),
      max: Math.max(leftHandlePosition, rightHandlePosition),
    };
  }, [leftHandlePosition, rightHandlePosition]);

  /** (onBlur) 입력값과 슬라이더 위치를 업데이트 */
  const updateInputAndHandle = (min: number, max: number): void => {
    const [minVal, maxVal] = [roundDownToHundred(min), roundDownToHundred(max)];

    setMinInputValue(numberFormat(minVal));
    setMaxInputValue(numberFormat(maxVal));

    // 입력값에 따라 핸들 위치 조정
    const minPositionPercent = calculatePosition({ bins, value: minVal, range });
    const maxPositionPercent = calculatePosition({ bins, value: maxVal, range });

    setLeftHandlePosition(minPositionPercent);
    setRightHandlePosition(maxPositionPercent);

    onRangeChange(minVal, maxVal);
  };

  const handleMinInputBlur = (e: number): void => {
    let [minVal, maxVal] = [e, Number(getOnlyNumbers(maxInputValue))];
    if (e < range.min) {
      minVal = range.min; // 최소값보다 작으면 최소값으로 설정
    } else if (e > range.max) {
      // 최대값보다 크면 최대값으로 설정
      maxVal = range.max;
      minVal = range.max;
    } else if (e > maxVal) {
      if (hasItems(bins)) {
        // 히스토그램 정보 있을때
        // 최대 필드는 최소 필드 기준의 슬라이더 컨트롤러가 위치한 클러스터의 max값으로 보정
        const clusterIdx = bins.findIndex((bin) => bin.from <= e && e <= bin.to);
        maxVal = bins[clusterIdx].to;
      } else {
        // 히스토그램 정보 없을때 (=히스토그램이 미노출되는 경우)
        // 최대 필드는 최대값으로 보정
        maxVal = range.max;
      }
    }
    updateInputAndHandle(minVal, maxVal);
  };

  const handleMaxInputBlur = (e: number): void => {
    let [minVal, maxVal] = [Number(getOnlyNumbers(minInputValue)), e];
    if (e < range.min) {
      // 최소값보다 작으면 최소값으로 설정
      minVal = range.min;
      maxVal = range.min;
    } else if (e > range.max) {
      // 최대값보다 크면 최대값으로 설정
      maxVal = range.max;
    } else if (e < minVal) {
      if (hasItems(bins)) {
        // 히스토그램 정보 있을때
        // 최소 필드는 최대 필드 기준의 슬라이더 컨트롤러가 위치한 클러스터의 min값으로 보정
        const clusterIdx = bins.findIndex((bin) => bin.from <= e && e <= bin.to);
        minVal = bins[clusterIdx].from;
      } else {
        // 히스토그램 정보 없을때 (=히스토그램이 미노출되는 경우)
        // 최소 필드는 최소값으로 보정
        minVal = range.min;
      }
    }
    updateInputAndHandle(minVal, maxVal);
  };

  const handleOnFocusHistogramInput = (minMax: 'min' | 'max'): void => {
    onFocusHistogramInput(item.serverLogMeta, minMax);
  };

  const resetInputAndHandle = useCallback(() => {
    setMinInputValue(numberFormat(range.min));
    setMaxInputValue(numberFormat(range.max));

    setLeftHandlePosition(0);
    setRightHandlePosition(100);
  }, [range.min, range.max]);

  useEffect(() => {
    if (isSelected) return;
    // 초기화 버튼 클릭 시, 입력값과 핸들 위치 초기화
    resetInputAndHandle();
  }, [isSelected, resetInputAndHandle]);

  useEffect(() => {
    if (!draggingHandle) return;
    const endEvents = ['mouseup', 'touchend', 'touchcancel'];
    const moveEvents = ['mousemove', 'touchmove'];

    moveEvents.forEach((event) => {
      document.addEventListener(event, onHandleMouseMove as EventListener);
    });
    endEvents.forEach((event) => {
      document.addEventListener(event, onHandleMouseUp);
    });

    return () => {
      moveEvents.forEach((event) => {
        document.removeEventListener(event, onHandleMouseMove as EventListener);
      });
      endEvents.forEach((event) => {
        document.removeEventListener(event, onHandleMouseUp);
      });
    };
  }, [draggingHandle]);

  return {
    histogramRef,

    draggingHandle,
    handlePosition,
    zIndexLeft,
    zIndexRight,

    leftHandlePosition,
    rightHandlePosition,
    onHandleDragStart,
    handleMinInputBlur,
    handleMaxInputBlur,
    handleOnFocusHistogramInput,

    minInputValue,
    setMinInputValue,
    maxInputValue,
    setMaxInputValue,
  };
};

export default useHistogramDragHandler;
